import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./ApiMonitor.css";
import L from "leaflet";

// Fix leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const COLORS = ["#ffffff", "#ff0000"];

const ApiMonitor = () => {
  const [config, setConfig] = useState({
    baseUrl: "https://jsonplaceholder.typicode.com",
    testEndpoint: "/posts/1",
    authToken: "",
    httpMethod: "GET",
    requestBody: "",
    successStatusCodes: "200,201,202,204",
    saveResponses: false,
    concurrentRequests: 10,
    batchSize: 100,
    timeout: 15000,
  });

  const [metrics, setMetrics] = useState({
    responseTimes: [],
    successCount: 0,
    failureCount: 0,
    totalRequests: 0,
    recentErrors: [],
    currentLocation: null,
    dnsInfo: null,
  });

  const [savedResponses, setSavedResponses] = useState([]);
  const [showResponseViewer, setShowResponseViewer] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const shouldStopRef = useRef(false);
  const startTimeRef = useRef(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Get current location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Read URL query parameters on mount
  useEffect(() => {
    const search = window.location.search;
    if (!search) return;

    const params = new URLSearchParams(search);
    const newConfig = { ...config };
    let hasParams = false;

    // Helper to get param with multiple possible names
    const getParam = (names) => {
      for (const name of names) {
        const value = params.get(name);
        if (value !== null) return value;
      }
      return null;
    };

    // Base URL
    const baseUrl = getParam(["baseUrl", "base_url"]);
    if (baseUrl) {
      newConfig.baseUrl = decodeURIComponent(baseUrl);
      hasParams = true;
    }

    // Endpoint
    const endpoint = getParam(["testEndpoint", "endpoint"]);
    if (endpoint) {
      newConfig.testEndpoint = decodeURIComponent(endpoint);
      hasParams = true;
    }

    // Auth Token
    const authToken = getParam(["authToken", "auth_token", "token"]);
    if (authToken) {
      newConfig.authToken = decodeURIComponent(authToken);
      hasParams = true;
    }

    // HTTP Method
    const method = getParam(["httpMethod", "http_method", "method"]);
    if (method) {
      newConfig.httpMethod = decodeURIComponent(method).toUpperCase();
      hasParams = true;
    }

    // Request Body
    const body = getParam(["requestBody", "request_body", "body"]);
    if (body) {
      newConfig.requestBody = decodeURIComponent(body);
      hasParams = true;
    }

    // Success Status Codes
    const successCodes = getParam([
      "successStatusCodes",
      "success_codes",
      "success_status_codes",
    ]);
    if (successCodes) {
      newConfig.successStatusCodes = decodeURIComponent(successCodes);
      hasParams = true;
    }

    // Save Responses
    const saveResponses = getParam(["saveResponses", "save_responses"]);
    if (saveResponses) {
      newConfig.saveResponses =
        saveResponses === "true" || saveResponses === "1";
      hasParams = true;
    }

    // Concurrent Requests
    const concurrentRequests = getParam([
      "concurrentRequests",
      "concurrent_requests",
    ]);
    if (concurrentRequests) {
      newConfig.concurrentRequests = parseInt(concurrentRequests) || 10;
      hasParams = true;
    }

    // Batch Size
    const batchSize = getParam(["batchSize", "batch_size"]);
    if (batchSize) {
      newConfig.batchSize = parseInt(batchSize) || 100;
      hasParams = true;
    }

    // Timeout
    const timeout = getParam(["timeout"]);
    if (timeout) {
      newConfig.timeout = parseInt(timeout) || 15000;
      hasParams = true;
    }

    if (hasParams) {
      setConfig(newConfig);
    }
  }, []);

  const getCurrentLocation = async () => {
    try {
      const response = await axios.get("http://ip-api.com/json/", {
        timeout: 5000,
      });

      if (response.data && response.data.status === "success") {
        setMetrics((prev) => ({
          ...prev,
          currentLocation: {
            name: `${response.data.city}, ${response.data.country}`,
            lat: response.data.lat,
            lng: response.data.lon,
            country: response.data.country,
            region: response.data.regionName,
            city: response.data.city,
            isp: response.data.isp,
            timezone: response.data.timezone,
            ip: response.data.query,
            dns: response.data.dns || "Auto",
          },
        }));
      }
    } catch (error) {
      console.error("Location detection failed:", error);
      // Set default location
      setMetrics((prev) => ({
        ...prev,
        currentLocation: {
          name: "Istanbul, Turkey",
          lat: 41.0606463,
          lng: 28.9627561,
          country: "Turkey",
          isp: "Unknown",
          timezone: "Unknown",
          ip: "Unknown",
          dns: "System Default",
        },
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "concurrentRequests" ||
            name === "timeout" ||
            name === "batchSize"
          ? parseInt(value) || 0
          : value,
    }));
  };

  const generateShareUrl = () => {
    const params = new URLSearchParams();

    params.set("baseUrl", config.baseUrl);
    params.set("endpoint", config.testEndpoint);

    if (config.authToken) {
      params.set("token", config.authToken);
    }

    params.set("method", config.httpMethod);

    if (config.requestBody) {
      params.set("body", config.requestBody);
    }

    params.set("success_codes", config.successStatusCodes);
    params.set("save_responses", config.saveResponses ? "true" : "false");
    params.set("concurrent_requests", config.concurrentRequests.toString());
    params.set("batch_size", config.batchSize.toString());
    params.set("timeout", config.timeout.toString());

    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?${params.toString()}`;
  };

  const handleShare = async () => {
    const shareUrl = generateShareUrl();

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const makeRequest = async (requestId) => {
    const startTime = Date.now();

    try {
      const requestConfig = {
        timeout: config.timeout,
        headers: {
          "Content-Type": "application/json",
        },
      };

      if (config.authToken) {
        const token = config.authToken.startsWith("Bearer ")
          ? config.authToken
          : `Bearer ${config.authToken}`;
        requestConfig.headers.Authorization = token;
      }

      // Add request body for POST, PUT, PATCH methods
      if (
        config.requestBody &&
        ["POST", "PUT", "PATCH"].includes(config.httpMethod)
      ) {
        try {
          requestConfig.data = JSON.parse(config.requestBody);
        } catch (e) {
          // If not valid JSON, send as string
          requestConfig.data = config.requestBody;
        }
      }

      if (metrics.currentLocation) {
        requestConfig.params = {
          lat: metrics.currentLocation.lat,
          lng: metrics.currentLocation.lng,
        };
      }

      const response = await axios({
        method: config.httpMethod,
        url: `${config.baseUrl}${config.testEndpoint}`,
        ...requestConfig,
      });

      const endTime = Date.now();
      const duration = endTime - startTime;

      // Check if status code is in the success list
      const allowedCodes = config.successStatusCodes
        .split(",")
        .map((code) => parseInt(code.trim()))
        .filter((code) => !isNaN(code));

      const isSuccess =
        allowedCodes.length > 0
          ? allowedCodes.includes(response.status)
          : response.status >= 200 && response.status < 300;

      const result = {
        id: requestId,
        success: isSuccess,
        status: response.status,
        duration: duration,
        timestamp: new Date(),
        dataSize: JSON.stringify(response.data).length,
      };

      // Save failed response if enabled
      if (config.saveResponses && !isSuccess) {
        setSavedResponses((prev) => [
          ...prev,
          {
            ...result,
            request: {
              method: config.httpMethod,
              url: `${config.baseUrl}${config.testEndpoint}`,
              headers: response.config.headers,
              body: config.requestBody || null,
            },
            response: {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
              data: response.data,
            },
          },
        ]);
      }

      return result;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;

      const errorResult = {
        id: requestId,
        success: false,
        status: error.response?.status || 0,
        duration: duration,
        error: error.message,
        timestamp: new Date(),
      };

      // Save failed response if enabled
      if (config.saveResponses) {
        setSavedResponses((prev) => [
          ...prev,
          {
            ...errorResult,
            request: {
              method: config.httpMethod,
              url: `${config.baseUrl}${config.testEndpoint}`,
              headers: error.config?.headers || {},
              body: config.requestBody || null,
            },
            response: {
              status: error.response?.status || 0,
              statusText: error.response?.statusText || error.message,
              headers: error.response?.headers || {},
              data: error.response?.data || { error: error.message },
            },
          },
        ]);
      }

      return errorResult;
    }
  };

  const runConcurrentTest = async () => {
    setIsRunning(true);
    shouldStopRef.current = false;
    startTimeRef.current = new Date(); // Store start time in ref
    setSavedResponses([]); // Clear previous saved responses
    setMetrics((prev) => ({
      ...prev,
      responseTimes: [],
      successCount: 0,
      failureCount: 0,
      totalRequests: 0,
      recentErrors: [],
    }));
    setProgress(0);

    const batchSize = config.batchSize;
    const totalRequests = config.concurrentRequests;
    const batches = Math.ceil(totalRequests / batchSize);

    let completedRequests = 0;

    for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
      if (shouldStopRef.current) {
        addError(
          "Test Stopped",
          `Stopped at ${completedRequests} requests`,
          "INFO"
        );
        break;
      }

      const batchStart = batchIndex * batchSize;
      const batchEnd = Math.min(batchStart + batchSize, totalRequests);

      const promises = [];
      for (let i = batchStart; i < batchEnd; i++) {
        promises.push(makeRequest(i));
      }

      try {
        const results = await Promise.allSettled(promises);
        completedRequests += results.length;

        processResults(results);
        setProgress((completedRequests / totalRequests) * 100);

        if (batchIndex < batches - 1 && !shouldStopRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        addError(
          "Batch Error",
          `Batch ${batchIndex + 1} failed: ${error.message}`,
          "ERROR"
        );
      }
    }

    setIsRunning(false);
    if (!shouldStopRef.current) {
      addError(
        "Test Complete",
        `All ${totalRequests} requests completed`,
        "SUCCESS"
      );
    }
  };

  const stopTest = () => {
    shouldStopRef.current = true;
  };

  const processResults = (results) => {
    const successful = results.filter(
      (r) => r.status === "fulfilled" && r.value.success
    );
    const failed = results.filter(
      (r) => r.status === "rejected" || !r.value?.success
    );

    setMetrics((prev) => {
      const responseTimes = successful.map((r) => r.value.duration);
      const newResponseTimes = [...prev.responseTimes, ...responseTimes].slice(
        -50
      );

      // Add errors to recent errors
      const newErrors = [...prev.recentErrors];
      failed.forEach((result) => {
        const error =
          result.status === "rejected" ? result.reason : result.value;
        const statusCode = error.status || error.response?.status || "NETWORK";
        const errorMessage = error.error || error.message || "Unknown error";

        newErrors.unshift({
          type: "API Request",
          message: errorMessage,
          errorCode: statusCode,
          timestamp: new Date(),
        });
      });

      return {
        ...prev,
        responseTimes: newResponseTimes,
        successCount: prev.successCount + successful.length,
        failureCount: prev.failureCount + failed.length,
        totalRequests: prev.totalRequests + results.length,
        recentErrors: newErrors.slice(0, 10),
      };
    });
  };

  const addError = (type, message, errorCode = null) => {
    setMetrics((prev) => ({
      ...prev,
      recentErrors: [
        {
          type,
          message,
          errorCode,
          timestamp: new Date(),
        },
        ...prev.recentErrors,
      ].slice(0, 10),
    }));
  };

  const successRate =
    metrics.totalRequests > 0
      ? Math.round((metrics.successCount / metrics.totalRequests) * 100)
      : 100;

  const avgResponseTime =
    metrics.responseTimes.length > 0
      ? Math.round(
          metrics.responseTimes.reduce((a, b) => a + b, 0) /
            metrics.responseTimes.length
        )
      : 0;

  const chartData = metrics.responseTimes.map((time, index) => ({
    request: index + 1,
    responseTime: time,
  }));

  const pieData = [
    { name: "Success", value: metrics.successCount },
    { name: "Failed", value: metrics.failureCount },
  ];

  return (
    <div className="container">
      <div className="api-monitor-container">
        <div className="monitor-header">
          <h1>🚀 API Monitor Dashboard</h1>
          <p>Real-time monitoring for any API endpoint</p>
        </div>

        <div className="input-section">
          <div className="input-grid">
            <div className="input-group">
              <label>Base URL *</label>
              <input
                type="text"
                name="baseUrl"
                value={config.baseUrl}
                onChange={handleInputChange}
                placeholder="https://api.example.com"
                disabled={isRunning}
              />
            </div>

            <div className="input-group">
              <label>Endpoint *</label>
              <input
                type="text"
                name="testEndpoint"
                value={config.testEndpoint}
                onChange={handleInputChange}
                placeholder="/api/health"
                disabled={isRunning}
              />
            </div>

            <div className="input-group">
              <label>Auth Token (Optional)</label>
              <input
                type="password"
                name="authToken"
                value={config.authToken}
                onChange={handleInputChange}
                placeholder="Bearer token or just token"
                disabled={isRunning}
              />
            </div>

            <div className="input-group">
              <label>HTTP Method</label>
              <select
                name="httpMethod"
                value={config.httpMethod}
                onChange={handleInputChange}
                disabled={isRunning}
                className="method-select"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
                <option value="HEAD">HEAD</option>
                <option value="OPTIONS">OPTIONS</option>
              </select>
            </div>

            {["POST", "PUT", "PATCH"].includes(config.httpMethod) && (
              <div className="input-group full-width">
                <label>Request Body (Optional JSON)</label>
                <textarea
                  name="requestBody"
                  value={config.requestBody}
                  onChange={handleInputChange}
                  placeholder='{"key": "value"}'
                  disabled={isRunning}
                  className="body-textarea"
                  rows="3"
                />
              </div>
            )}

            <div className="input-group">
              <label>Success Status Codes</label>
              <input
                type="text"
                name="successStatusCodes"
                value={config.successStatusCodes}
                onChange={handleInputChange}
                placeholder="200,201,204"
                disabled={isRunning}
              />
            </div>

            <div className="input-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="saveResponses"
                  checked={config.saveResponses}
                  onChange={handleInputChange}
                  disabled={isRunning}
                />
                <span>Save Failed Responses</span>
              </label>
            </div>

            <div className="input-group">
              <label>Concurrent Requests</label>
              <input
                type="number"
                name="concurrentRequests"
                value={config.concurrentRequests}
                onChange={handleInputChange}
                min="1"
                max="10000"
                disabled={isRunning}
              />
            </div>

            <div className="input-group">
              <label>Batch Size</label>
              <input
                type="number"
                name="batchSize"
                value={config.batchSize}
                onChange={handleInputChange}
                min="1"
                max="1000"
                disabled={isRunning}
              />
            </div>

            <div className="input-group">
              <label>Timeout (ms)</label>
              <input
                type="number"
                name="timeout"
                value={config.timeout}
                onChange={handleInputChange}
                min="1000"
                max="60000"
                disabled={isRunning}
              />
            </div>
          </div>

          <div className="button-group">
            {!isRunning && (
              <div className="input-group">
                <button
                  className="start-button"
                  onClick={runConcurrentTest}
                  disabled={
                    isRunning || !config.baseUrl || !config.testEndpoint
                  }
                >
                  {isRunning ? "Running..." : "Start Test"}
                </button>
              </div>
            )}

            {isRunning && (
              <div className="input-group">
                <button className="stop-button" onClick={stopTest}>
                  Stop Test
                </button>
              </div>
            )}
            {isRunning && (
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
                <span className="progress-text">{Math.round(progress)}%</span>
              </div>
            )}
            <button
              className="share-button"
              onClick={handleShare}
              title="Share Configuration"
            >
              {copyFeedback ? "✓" : "🔗"}
            </button>
          </div>
        </div>

        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Total Requests</h3>
            <div className="metric-value">{metrics.totalRequests}</div>
          </div>

          <div className="metric-card">
            <h3>Successful</h3>
            <div className="metric-value">{metrics.successCount}</div>
          </div>

          <div className="metric-card">
            <h3>Failed</h3>
            <div
              className="metric-value"
              style={{ color: metrics.failureCount > 0 ? "#ff0000" : "#fff" }}
            >
              {metrics.failureCount}
            </div>
          </div>

          <div className="metric-card">
            <h3>Success Rate</h3>
            <div className="metric-value">{successRate}%</div>
          </div>

          <div className="metric-card">
            <h3>Avg Response Time</h3>
            <div className="metric-value">{avgResponseTime}ms</div>
          </div>

          {/* {metrics.startTime && (
            <div className="metric-card">
              <h3>Duration</h3>
              <div className="metric-value">
                {Math.round((new Date() - metrics.startTime) / 1000)}s
              </div>
            </div>
          )} */}
        </div>

        <div className="charts-section">
          <div className="chart-container">
            <h3>Response Time Chart</h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="request" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#000",
                      border: "1px solid #333",
                      color: "#fff",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="responseTime"
                    stroke="#fff"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                No data yet. Run a test to see results.
              </div>
            )}
          </div>

          <div className="chart-container">
            <h3>Success vs Failed Requests</h3>
            {metrics.totalRequests > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#000",
                      border: "1px solid #333",
                      color: "#fff",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                No data yet. Run a test to see results.
              </div>
            )}
          </div>
        </div>

        {metrics.currentLocation && (
          <div className="map-section">
            <h3>📍 Test Location: {metrics.currentLocation.name}</h3>
            <MapContainer
              center={[
                metrics.currentLocation.lat,
                metrics.currentLocation.lng,
              ]}
              zoom={10}
              style={{ height: "400px", width: "100%", borderRadius: "8px" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker
                position={[
                  metrics.currentLocation.lat,
                  metrics.currentLocation.lng,
                ]}
              >
                <Popup>
                  <strong>{metrics.currentLocation.name}</strong>
                  <br />
                  ISP: {metrics.currentLocation.isp}
                  <br />
                  Timezone: {metrics.currentLocation.timezone}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        )}

        <div className="activity-section">
          <h3>Activity Log</h3>
          <div className="activity-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Code</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recentErrors.length > 0 ? (
                  metrics.recentErrors.map((error, index) => (
                    <tr
                      key={index}
                      className={
                        error.errorCode === "SUCCESS"
                          ? "success-row"
                          : "error-row"
                      }
                    >
                      <td>{moment(error.timestamp).format("HH:mm:ss")}</td>
                      <td>{error.type}</td>
                      <td>{error.errorCode || "N/A"}</td>
                      <td>{error.message}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      No activity yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {metrics.currentLocation && (
          <div className="network-info">
            <h3>Network Information</h3>
            <div className="info-sections">
              <div className="info-section">
                <h4 className="section-title">Origin</h4>
                <div className="info-content">
                  <div className="info-row">
                    <span className="info-label">IP Address</span>
                    <span className="info-value">
                      {metrics.currentLocation.ip || "Unknown"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">DNS</span>
                    <span className="info-value">
                      {metrics.currentLocation.dns || "System Default"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Location</span>
                    <span className="info-value">
                      {metrics.currentLocation.name}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">ISP</span>
                    <span className="info-value">
                      {metrics.currentLocation.isp}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Timezone</span>
                    <span className="info-value">
                      {metrics.currentLocation.timezone}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Coordinates</span>
                    <span className="info-value">
                      {metrics.currentLocation.lat.toFixed(4)},{" "}
                      {metrics.currentLocation.lng.toFixed(4)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4 className="section-title">Target</h4>
                <div className="info-content">
                  <div className="info-row">
                    <span className="info-label">Base URL</span>
                    <span className="info-value">{config.baseUrl}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Endpoint</span>
                    <span className="info-value">{config.testEndpoint}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">HTTP Method</span>
                    <span className="info-value">{config.httpMethod}</span>
                  </div>
                  {config.requestBody && (
                    <div className="info-row">
                      <span className="info-label">Request Body</span>
                      <span className="info-value">Included</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-label">Authentication</span>
                    <span className="info-value">
                      {config.authToken ? "Enabled" : "None"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Timeout</span>
                    <span className="info-value">{config.timeout}ms</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4 className="section-title">Configuration</h4>
                <div className="info-content">
                  <div className="info-row">
                    <span className="info-label">Total Requests</span>
                    <span className="info-value">
                      {config.concurrentRequests}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Batch Size</span>
                    <span className="info-value">{config.batchSize}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Batches</span>
                    <span className="info-value">
                      {Math.ceil(config.concurrentRequests / config.batchSize)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Success Codes</span>
                    <span className="info-value">
                      {config.successStatusCodes}
                    </span>
                  </div>
                  {startTimeRef.current && (
                    <div className="info-row">
                      <span className="info-label">Started</span>
                      <span className="info-value">
                        {moment(startTimeRef.current).format("HH:mm:ss")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {config.saveResponses && savedResponses.length > 0 && (
          <div className="saved-responses-section">
            <div className="section-header">
              <h3>Saved Failed Responses ({savedResponses.length})</h3>
              <button
                className="view-responses-button"
                onClick={() => {
                  setSelectedResponse(
                    savedResponses[savedResponses.length - 1]
                  );
                  setShowResponseViewer(true);
                }}
              >
                View Failed Responses
              </button>
            </div>
          </div>
        )}

        {showResponseViewer && (
          <div
            className="response-viewer-modal"
            onClick={() => setShowResponseViewer(false)}
          >
            <div
              className="response-viewer-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Failed Response Viewer</h2>
                <button
                  className="close-button"
                  onClick={() => setShowResponseViewer(false)}
                >
                  ×
                </button>
              </div>

              <div className="response-navigation">
                <button
                  onClick={() => {
                    const currentIndex = savedResponses.findIndex(
                      (r) => r.id === selectedResponse?.id
                    );
                    if (currentIndex > 0) {
                      setSelectedResponse(savedResponses[currentIndex - 1]);
                    }
                  }}
                  disabled={
                    !selectedResponse ||
                    savedResponses.findIndex(
                      (r) => r.id === selectedResponse?.id
                    ) === 0
                  }
                >
                  ← Previous
                </button>
                <span>
                  {selectedResponse
                    ? savedResponses.findIndex(
                        (r) => r.id === selectedResponse.id
                      ) + 1
                    : 0}{" "}
                  / {savedResponses.length}
                </span>
                <button
                  onClick={() => {
                    const currentIndex = savedResponses.findIndex(
                      (r) => r.id === selectedResponse?.id
                    );
                    if (currentIndex < savedResponses.length - 1) {
                      setSelectedResponse(savedResponses[currentIndex + 1]);
                    }
                  }}
                  disabled={
                    !selectedResponse ||
                    savedResponses.findIndex(
                      (r) => r.id === selectedResponse?.id
                    ) ===
                      savedResponses.length - 1
                  }
                >
                  Next →
                </button>
              </div>

              {selectedResponse && (
                <div className="response-details">
                  <div className="detail-section">
                    <h3>Request</h3>
                    <div className="detail-content">
                      <div className="detail-row">
                        <span className="detail-label">Method:</span>
                        <span className="detail-value">
                          {selectedResponse.request.method}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">URL:</span>
                        <span className="detail-value">
                          {selectedResponse.request.url}
                        </span>
                      </div>
                      {selectedResponse.request.body && (
                        <div className="detail-row">
                          <span className="detail-label">Body:</span>
                          <pre className="detail-value">
                            {selectedResponse.request.body}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Response</h3>
                    <div className="detail-content">
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span
                          className={`detail-value ${
                            selectedResponse.success
                              ? "success-text"
                              : "error-text"
                          }`}
                        >
                          {selectedResponse.response.status}{" "}
                          {selectedResponse.response.statusText}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">
                          {selectedResponse.duration}ms
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Time:</span>
                        <span className="detail-value">
                          {moment(selectedResponse.timestamp).format(
                            "HH:mm:ss.SSS"
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Size:</span>
                        <span className="detail-value">
                          {selectedResponse.dataSize} bytes
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Response Body</h3>
                    <pre className="response-body">
                      {JSON.stringify(selectedResponse.response.data, null, 2)}
                    </pre>
                  </div>

                  <div className="detail-section">
                    <h3>Response Headers</h3>
                    <pre className="response-headers">
                      {JSON.stringify(
                        selectedResponse.response.headers,
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiMonitor;
